import React, { useEffect, useRef, useState, useCallback } from "react";
import { Check, Sparkles, Zap, Rocket, Crown, Star, Loader2, ExternalLink } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserProfile,
  initiateCheckout,
  createSubscription,
  getSubscriptionPlans,
  fetchMySubscriptions,
  cancelSubscription,
  getSubscriptionUsage,
  getChargebeePortal,
  fetchSubscriptionUsage,
} from "../../redux/network/userApi";
import { setAlertMessage, setAlertType } from "../../redux/reducers/alertSlice";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import ContactFormModal from "../ContactForm/ContactFormModal";

// ─── Icon helpers ─────────────────────────────────────────────────────────────
const PLAN_ICONS = [Rocket, Zap, Sparkles, Star, Crown];

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    fontSize: '9px',
    fontWeight: 700,
    border: '1px solid #f1f5f9',
    borderRadius: '10px',
    padding: '6px 10px',
    maxWidth: 180,
    textAlign: 'center',
    lineHeight: '1.3',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
    "&::before": {
      border: "1px solid #e2e8f0",
    },
  },
}));

// ─── Feature label maps ───────────────────────────────────────────────────────
function buildInsightsFeatures(f = {}) {
  const list = [];

  if (f.maxVideoUploads) list.push(`${f.maxVideoUploads} Video Upload${f.maxVideoUploads > 1 ? "s" : ""}`);
  list.push("Max video size: 2 GB");
  if (f.maxFeedbackReports) list.push(`${f.maxFeedbackReports} Feedback Report${f.maxFeedbackReports > 1 ? "s" : ""}`);
  if (f.videoStorageGB) list.push(`${f.videoStorageGB} GB Video Storage`);
  if (f.journalSubscription) list.push("Smart Journal Subscription");
  if (f.setVideo) list.push("Video Analysis");
  if (f.videoHighlights) list.push("Video Highlights");
  if (f.summaryReport) list.push("Summary Report");
  if (f.detailedReport) list.push("Detailed Report & Dashboard");
  if (f.askVisist) list.push("Ask VISIST Feature");
  if (f.injuryReport) list.push("Injury Analysis");
  if (f.priorityFeedback) list.push("Priority Feedback");
  if (f.historicalTracking) list.push("Historical Tracking");
  if (f.customStrategyReports) list.push("Custom Strategy Reports");
  return list;
}

function buildAssessmentFeatures(q = {}) {
  const list = [];
  if (q.maxAssessments) list.push(`${q.maxAssessments} Assessment${q.maxAssessments > 1 ? "s" : ""}`);
  if (q.enabledTypes?.length) list.push(`Types: ${q.enabledTypes.join(", ")}`);
  return list;
}

function getPlanFeatures(plan) {
  if (plan.insightsFeatures) return buildInsightsFeatures(plan.insightsFeatures);
  if (plan.assessmentQuota) return buildAssessmentFeatures(plan.assessmentQuota);
  return [];
}

function getValidityLabel(days) {
  if (!days) return "";
  if (days >= 36500) return "Lifetime";
  if (days >= 365) return `${Math.round(days / 365)} Year${Math.round(days / 365) > 1 ? "s" : ""} Validity`;
  if (days >= 28) return `${Math.round(days / 30)} Month Validity`;
  return `${days} Days Validity`;
}

// ─── Currency formatter ───────────────────────────────────────────────────────
const amountFormatter = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// ─── Animated counter ─────────────────────────────────────────────────────────
function animateValue(start, end, duration, onUpdate, store, index) {
  const startTime = { current: 0 };
  const step = (timestamp) => {
    if (!startTime.current) startTime.current = timestamp;
    const elapsed = timestamp - startTime.current;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 4);
    onUpdate(start + (end - start) * eased);
    if (progress < 1) store[index] = requestAnimationFrame(step);
  };
  store[index] = requestAnimationFrame(step);
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const ChevronRight = ({ size, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);


// ─── Skeleton card ────────────────────────────────────────────────────────────
function PlanSkeleton() {
  return (
    <div className="flex flex-col p-5 rounded-[2rem] border border-slate-200 bg-white animate-pulse shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="h-5 w-28 bg-slate-200 rounded mb-2" />
          <div className="h-3 w-20 bg-slate-100 rounded" />
        </div>
        <div className="w-10 h-10 rounded-2xl bg-slate-100" />
      </div>
      <div className="h-8 w-24 bg-slate-200 rounded mb-3" />
      <div className="h-4 w-32 bg-slate-100 rounded mb-4" />
      <div className="space-y-2 flex-1">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex gap-2 items-center">
            <div className="w-3.5 h-3.5 rounded-full bg-slate-100" />
            <div className="h-2.5 w-full bg-slate-100 rounded" />
          </div>
        ))}
      </div>
      <div className="h-10 mt-6 bg-slate-200 rounded-2xl" />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function SubscriptionPricing({ isForceUpgrade = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  // ?manage=1 means the user deliberately navigated here (e.g. from sidebar)
  // In that case we skip the auto-redirect and always show plans with active plan highlighted
  const isManageMode = new URLSearchParams(location.search).get("manage") === "1";

  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.profile);
  const profileData = userData?.value?.profileData;
  const jwtToken = localStorage.getItem("BQS_TOKEN");

  const [allPlans, setAllPlans] = useState([]);
  const subscriptionsState = useSelector((state) => state.user.subscriptions);
  const activeSubs = subscriptionsState.value || [];

  const usageState = useSelector((state) => state.user.subscriptionUsage);
  const usageData = usageState.value;
  const isUsageLoading = usageState.isStarted;
  const allSubscriptionsState = useSelector((state) => state.user.allSubscriptions);
  const allSubsHistory = allSubscriptionsState?.value || [];
  const hasUsedPromoOrPaid = allSubsHistory.length > 0;
  const [loading, setLoading] = useState(true);
  const [isActivating, setIsActivating] = useState(null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelSubId, setCancelSubId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [openLetsTalk, setOpenLetsTalk] = useState(false);


  // Gate states: "checking" | "redirect" | "active" | "exhausted" | "none"
  // - checking  : fetching existing subscription on mount
  // - active    : manage mode — show plans with active one highlighted
  // - redirect  : active sub with quota → will redirect to My Journal
  // - exhausted : active sub but quota used up → show upgrade page
  // - none      : no active sub → first time, show plans normally
  const [gateStatus, setGateStatus] = useState("checking");

  // Price animation refs
  const priceRefs = useRef([]);
  const amountRafRefs = useRef([]);
  const pollIntervalRef = useRef(null);
  const focusListenerRef = useRef(null);
  const initialSubIdRef = useRef(activeSubs[0]?._id);
  const isSubscribingRef = useRef(false);
  const isOpeningPortalRef = useRef(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // AUTO-STOP POLLING: If the primary subscription ID changes, stop all polling/listeners
  useEffect(() => {
    const currentSubId = activeSubs[0]?._id;
    if (currentSubId && currentSubId !== initialSubIdRef.current) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (focusListenerRef.current) {
        window.removeEventListener('focus', focusListenerRef.current);
        focusListenerRef.current = null;
      }
      initialSubIdRef.current = currentSubId;
    }
  }, [activeSubs]);

  // Trap back navigation when forced to upgrade
  useEffect(() => {
    if (isForceUpgrade) {
      window.history.pushState(null, "", window.location.href);
      const handlePopState = () => {
        window.history.pushState(null, "", window.location.href);
      };
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    }
  }, [isForceUpgrade]);



  useEffect(() => {
    const subs = subscriptionsState.value;
    if (jwtToken && Array.isArray(subs) && subs.length > 0 && !usageData && !isUsageLoading) {
      dispatch(fetchSubscriptionUsage(jwtToken, "smartInsights"));
    }
  }, [jwtToken, subscriptionsState.value, usageData, isUsageLoading, dispatch]);

  // ─── Subscription gate ────────────────────────────────────────────
  // On mount, check if the user already has an active subscription.
  // • Active + quota remaining  → redirect to My Journal immediately
  // • Active + quota exhausted  → stay on this page (show upgrade banner)
  // • No active sub             → stay on this page (first-time flow)
  useEffect(() => {
    // Initial fetch handled by Nav.jsx globally
  }, [jwtToken, dispatch]);

  useEffect(() => {
    const subs = subscriptionsState.value;
    if (subs === null) {
      if (subscriptionsState.isStarted) setGateStatus("checking");
      return;
    }

    if (subs.length === 0) {
      setGateStatus("none");
      return;
    }

    // In manage mode (from sidebar) or force upgrade, always show the page
    if (isManageMode || isForceUpgrade) {
      setGateStatus("active");
      return;
    }

    if (!usageData) {
      setGateStatus("checking");
      return;
    }

    // Check if ANY active sub still has remaining quota
    const hasQuota = subs.some((sub) => {
      if (usageData.error) return true; // Fallback to allowing access if fetch fails
      
      const insightsUsage = usageData.currentPeriod?.insightsUsage || {};
      const planFeatures = usageData.subscription?.plan?.insightsFeatures || sub.plan?.insightsFeatures;

      if (planFeatures) {
        const maxUploads = planFeatures.maxVideoUploads ?? Infinity;
        const used = insightsUsage?.videoUploads ?? 0;
        return used < maxUploads;
      }
      return true; // Default to true if not an insights plan
    });

    if (hasQuota) {
      setGateStatus("redirect");
      navigate("/game-posts/game-videos", { replace: true });
    } else {
      setGateStatus("exhausted");
    }
  }, [subscriptionsState.value, subscriptionsState.isStarted, isManageMode, navigate, usageData]);


  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSubscriptionPlans();
      if (res?.code === 200 && Array.isArray(res.data)) {
        setAllPlans(res.data);
      } else {
        dispatch(setAlertMessage(res?.message || "Failed to load plans."));
        dispatch(setAlertType("error"));
      }
    } catch {
      dispatch(setAlertMessage("Failed to load plans."));
      dispatch(setAlertType("error"));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  // Classification helpers
  const ACADEMY_PLAN_CODES = new Set(["super_25", "super_100", "super_500"]);
  const isAcademyPlan = (plan) =>
    ACADEMY_PLAN_CODES.has(plan.code) || (plan.insightsFeatures?.maxSeats ?? 1) > 1;

  // All individual (non-academy) plans — no billing cycle filter
  const individualPlans = allPlans.filter((p) => !isAcademyPlan(p));

  // Animate prices for all currently rendered plans
  const visiblePlans = individualPlans;

  useEffect(() => {
    amountRafRefs.current.forEach((id) => cancelAnimationFrame(id));
    amountRafRefs.current = [];

    visiblePlans.forEach((plan, index) => {
      if (plan.price === 0) return;
      animateValue(0, plan.price, 1200, (value) => {
        if (priceRefs.current[index]) {
          priceRefs.current[index].innerText = amountFormatter.format(Math.round(value)) + "/-";
        }
      }, amountRafRefs.current, index);
    });

    return () => amountRafRefs.current.forEach((id) => cancelAnimationFrame(id));
  }, [visiblePlans]);

  const handleSubscribe = async (plan) => {
    if (isSubscribingRef.current || isActivating) return;
    isSubscribingRef.current = true;
    setIsActivating(plan._id);

    const isFree = plan.price === 0;
    // First Step plan is a promo — activate directly without Chargebee
    const isPromoFree = plan.code === "insights-first-step";

    if (isFree || isPromoFree) {
      // Free / promo plan: POST /subscription with just planId
      const res = await createSubscription(jwtToken, plan._id);
      if (res?.code === 200) {
        dispatch(getUserProfile(jwtToken));
        dispatch(fetchMySubscriptions(jwtToken, "active", true));
        dispatch(setAlertMessage(`${plan.name} activated successfully!`));
        dispatch(setAlertType("success"));
        navigate("/game-posts/game-videos");
      } else {
        dispatch(setAlertMessage(res?.message || "Failed to activate plan."));
        dispatch(setAlertType("error"));
        setIsActivating(null);
        isSubscribingRef.current = false;
      }
    } else {
      // Paid plan: POST /subscription/checkout → open Chargebee in new tab
      const redirectUrl = window.location.origin + window.location.pathname + "#/subscription-success";
      const cancelUrl = window.location.origin + window.location.pathname + "#/subscription-failure";
      
      const res = await initiateCheckout(jwtToken, plan._id, profileData, redirectUrl, cancelUrl);
      if (res?.code === 200 && res?.data?.url) {
        window.open(res.data.url, '_blank');
        dispatch(setAlertMessage("Checkout opened! We'll update your status automatically once payment is complete."));
        dispatch(setAlertType("success"));
        setIsActivating(null);
        isSubscribingRef.current = false;

        // Start polling for 2 minutes (every 10s) until the plan changes
        initialSubIdRef.current = activeSubs[0]?._id;
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        
        let pollCount = 0;
        pollIntervalRef.current = setInterval(() => {
          pollCount += 1;
          dispatch(fetchMySubscriptions(jwtToken, "active", true));
          dispatch(fetchSubscriptionUsage(jwtToken, "smartInsights"));
          
          if (pollCount >= 12) { 
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }, 10000);
      } else {
        dispatch(setAlertMessage(res?.message || "Failed to initiate checkout."));
        dispatch(setAlertType("error"));
        setIsActivating(null);
        isSubscribingRef.current = false;
      }
    }
  };

  const handleCancelClick = (subId) => {
    setCancelSubId(subId);
    setCancelDialogOpen(true);
  };

  const confirmCancelSubscription = async () => {
    if (!cancelSubId) return;
    setIsCancelling(true);
    try {
      const res = await cancelSubscription(jwtToken, cancelSubId, "user requested cancellation");
      if (res?.code === 200) {
        dispatch(setAlertMessage("Subscription cancelled successfully."));
        dispatch(setAlertType("success"));
        dispatch(fetchMySubscriptions(jwtToken, "active", true));
      } else {
        dispatch(setAlertMessage(res?.message || "Failed to cancel subscription."));
        dispatch(setAlertType("error"));
      }
    } catch (err) {
      dispatch(setAlertMessage("An error occurred while cancelling."));
      dispatch(setAlertType("error"));
    } finally {
      setIsCancelling(false);
      setCancelDialogOpen(false);
      setCancelSubId(null);
    }
  };

  const handleManageBilling = async () => {
    if (isOpeningPortalRef.current || isOpeningPortal) return;
    isOpeningPortalRef.current = true;
    setIsOpeningPortal(true);
    try {
      const res = await getChargebeePortal(jwtToken);
      if (res?.code === 200) {
        // Try all common patterns
        let portalUrl = null;
        if (typeof res.data === 'string') portalUrl = res.data;
        else if (res.data?.url) portalUrl = res.data.url;
        else if (res.data?.portalUrl) portalUrl = res.data.portalUrl;
        else if (res.data?.accessUrl) portalUrl = res.data.accessUrl;
        else if (res.data?.access_url) portalUrl = res.data.access_url;
        else if (res.data?.portal_session?.access_url) portalUrl = res.data.portal_session.access_url;
        else if (typeof res.message === 'string' && res.message.startsWith('http')) portalUrl = res.message;
        else if (typeof res === 'string' && res.startsWith('http')) portalUrl = res;

        if (portalUrl) {
          window.open(portalUrl, "_blank");
        } else {
          // DEBUG: Show the entire response to figure out where the URL is hiding
          dispatch(setAlertMessage(`Failed to find URL. Response was: ${JSON.stringify(res)}`));
          dispatch(setAlertType("error"));
        }
      } else {
        dispatch(setAlertMessage(res?.message || "Failed to open billing portal."));
        dispatch(setAlertType("error"));
      }
    } catch (err) {
      dispatch(setAlertMessage("An error occurred."));
      dispatch(setAlertType("error"));
    } finally {
      setIsOpeningPortal(false);
      isOpeningPortalRef.current = false;
    }
  };

  // ─── Best Active Plan Helper ──────────────────────────────────────────────────
  const getBestActivePlanId = () => {
    if (!activeSubs || activeSubs.length === 0 || !allPlans) return null;
    
    // First, try to find the highest-priced EXPLICITLY active PAID plan
    let explicitlyActivePlanId = null;
    let explicitlyActiveMaxPrice = 0;
    for (const sub of activeSubs) {
      const rawStatus = sub.status || sub.state || '';
      const subStatus = String(rawStatus).trim().toLowerCase();
      if (subStatus !== 'active') continue;
      const planId = sub.plan?._id || sub.plan;
      const planDetails = allPlans.find(p => p._id === planId);
      const price = planDetails ? planDetails.price : (sub.plan?.price || 0);
      if (price > explicitlyActiveMaxPrice && price > 0) {
        explicitlyActiveMaxPrice = price;
        explicitlyActivePlanId = planId;
      }
    }
    
    // If they have an explicitly active PAID plan, use it
    if (explicitlyActivePlanId) return explicitlyActivePlanId;
    
    // Next, try to find the MOST RECENT CANCELLED (but unexpired) PAID plan
    let latestCancelledPlanId = null;
    let latestCancelledDate = -1;
    
    for (const sub of activeSubs) {
      const rawStatus = sub.status || sub.state || '';
      const subStatus = String(rawStatus || '').trim().toLowerCase();
      // Broad check for any cancellation-related status
      if (!subStatus.includes('cancel') && subStatus !== 'non_renewing') continue;
      const planId = sub.plan?._id || sub.plan;
      const planDetails = allPlans.find(p => p._id === planId);
      const price = planDetails ? planDetails.price : (sub.plan?.price || 0);
      
      if (price > 0) {
        // Extract the most reliable recent date property from the subscription object
        const dateVal = sub.updatedAt || sub.updated_at || sub.createdAt || sub.created_at || sub.activatedAt || sub.activated_at || 0;
        let dateMs = 0;
        if (typeof dateVal === 'string' && isNaN(Number(dateVal))) {
           dateMs = new Date(dateVal).getTime();
        } else if (dateVal) {
           const numericDate = Number(dateVal);
           dateMs = String(numericDate).length <= 10 ? numericDate * 1000 : numericDate;
        }

        // Keep the one with the latest date. If no dates exist, it defaults to the first one in the array.
        if (latestCancelledPlanId === null || dateMs > latestCancelledDate) {
          latestCancelledDate = dateMs;
          latestCancelledPlanId = planId;
        }
      }
    }
    
    // If they have an unexpired cancelled PAID plan, use the most recent one
    if (latestCancelledPlanId) return latestCancelledPlanId;

    // Finally, fallback to any free plan (or the highest priced plan if we missed something)
    let bestPlanId = null;
    let maxPrice = -1;
    for (const sub of activeSubs) {
      const planId = sub.plan?._id || sub.plan;
      const planDetails = allPlans.find(p => p._id === planId);
      const price = planDetails ? planDetails.price : (sub.plan?.price || 0);
      if (price > maxPrice) {
        maxPrice = price;
        bestPlanId = planId;
      }
    }
    return bestPlanId;
  };

  // Render helper for a single plan card
  const renderCard = (plan, index, featuredIdx, groupOffset = 0) => {
    const isFree = plan.price === 0;
    // First Step is a promo plan shown as free (₹199 struck-through)
    const isPromoFree = plan.code === "insights-first-step";
    
    // Only highlight the highest-tier active plan as the current plan, ignoring residual free plans
    const bestActivePlanId = getBestActivePlanId();
    const isActivePlan = bestActivePlanId === plan._id;
    const activeSubObj = isActivePlan ? activeSubs.find((s) => s.plan?._id === plan._id || s.plan === plan._id) : null;
    
    // First step plan is completely locked out if the user has ANY subscription history AND it's not currently active
    const isPromoLocked = isPromoFree && !isActivePlan && hasUsedPromoOrPaid;

    const isFeatured = index === featuredIdx && !isActivePlan;
    const PlanIcon = PLAN_ICONS[(groupOffset + index) % PLAN_ICONS.length];
    const features = getPlanFeatures(plan);
    const validityLabel = getValidityLabel(plan.validityDays);
    const isAnyLoading = isActivating !== null;
    const isLoading = isActivating === plan._id;
    const priceRefIdx = groupOffset + index;

    return (
      <div
        key={plan._id}
        className={`group relative flex flex-col p-5 rounded-[2rem] transition-all duration-500 ${isActivePlan
          ? "bg-emerald-50 ring-2 ring-[#247E45] shadow-[0_20px_40px_rgba(36,126,69,0.12)]"
          : isFeatured
            ? "bg-white ring-2 ring-slate-300 shadow-2xl shadow-slate-100"
            : "bg-white border border-slate-200 hover:border-[#247E45]/30 hover:shadow-lg"
          }`}
      >
        {/* Active Plan badge */}
        {isActivePlan && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-[#247E45] text-white text-[10px] font-bold rounded-full shadow-lg z-20 flex items-center gap-1.5 uppercase tracking-widest whitespace-nowrap">
            <Check size={12} strokeWidth={3} />
            Current Plan
          </div>
        )}
        {/* Recommended badge (non-active featured) */}
        {isFeatured && !isActivePlan && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-slate-700 text-white text-[10px] font-bold rounded-full shadow-lg z-20 flex items-center gap-1.5 uppercase tracking-widest">
            <Sparkles size={12} />
            RECOMMENDED
          </div>
        )}
        <div className="relative z-10 flex-1 flex flex-col">
          <div className="mb-4 flex items-center justify-center gap-4 text-center">
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-0.5">{plan.name}</h3>
              <p className="text-[9px] text-[#247E45] font-semibold uppercase tracking-wider">
                Individual Plan
              </p>
            </div>
            <div className={`p-2.5 rounded-xl ${isFeatured ? "bg-emerald-50 text-[#247E45]" : "bg-slate-50 text-slate-400"}`}>
              <PlanIcon size={18} />
            </div>
          </div>
          <div className="mb-5">
            <div className="flex items-baseline justify-center gap-2 flex-wrap">
              {isFree ? (
                <span className="text-2xl font-black text-[#247E45] tracking-tight">Free</span>
              ) : isPromoFree ? (
                // Promo plan: show struck-through original price + FREE badge
                <>
                  <span className="flex items-baseline gap-1 line-through decoration-[#247E45] decoration-2 text-slate-400">
                    <span className="text-base font-bold">₹</span>
                    <span className="text-xl font-bold">{amountFormatter.format(plan.price)}/-</span>
                  </span>
                  <span className="text-2xl font-black text-[#247E45] tracking-tight">Free</span>
                </>
              ) : (
                <>
                  <span className="text-xl font-bold text-slate-400">₹</span>
                  <span
                    className="text-2xl font-black text-slate-900 tracking-tight"
                    ref={(el) => (priceRefs.current[priceRefIdx] = el)}
                  >
                    {amountFormatter.format(plan.price)}/-
                  </span>
                </>
              )}
            </div>
            {!isFree && !isPromoFree && (
              <p className="text-[9px] text-slate-400 font-medium mt-0.5 tracking-tight text-center">Inclusive of all Taxes</p>
            )}
            {validityLabel && (
              <div className="mt-2 flex flex-col gap-1.5">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-slate-400 text-[10px] font-medium italic">{validityLabel}</span>
                  {!isFree && !isPromoFree && (
                    <div className="h-3 w-px bg-slate-200" />
                  )}
                  {!isFree && !isPromoFree && (
                    <LightTooltip 
                      title={`Renews automatically ${plan.validityDays >= 365 ? 'every 12 months' : plan.validityDays >= 30 ? `every ${Math.floor(plan.validityDays/30)} month(s)` : `every ${plan.validityDays} days`} if not cancelled.`}
                      arrow
                      placement="top"
                      PopperProps={{
                        disablePortal: true,
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 8],
                            },
                          },
                          {
                            name: 'preventOverflow',
                            options: {
                              boundary: 'clippingParents',
                              padding: 8,
                            },
                          },
                        ],
                      }}
                    >
                      <div className="flex items-center gap-1 text-[9px] text-[#247E45] font-black uppercase tracking-tighter opacity-80 cursor-help hover:opacity-100 transition-opacity">
                        <div className="w-1 h-1 rounded-full bg-[#247E45] animate-pulse" />
                        Auto-renews
                      </div>
                    </LightTooltip>
                  )}
                </div>
              </div>
            )}
            <button
              onClick={() => !isActivePlan && !isPromoLocked && !isAnyLoading && handleSubscribe(plan)}
              disabled={isAnyLoading || isActivePlan || isPromoLocked}
              className={`mt-3 w-full py-2.5 px-6 rounded-2xl font-bold tracking-widest text-[10px] uppercase transition-all duration-300 flex items-center justify-center gap-2 group/btn shadow-md disabled:cursor-not-allowed ${isActivePlan || isPromoLocked || (isAnyLoading && !isLoading)
                ? "bg-slate-100 text-slate-400 shadow-none border border-slate-200 opacity-100"
                : isPromoFree
                  ? "bg-[#247E45] hover:bg-[#1f6b3a] text-white shadow-emerald-200/50 ring-2 ring-emerald-300"
                  : "bg-[#247E45] hover:bg-[#1f6b3a] text-white shadow-emerald-200/50"
                }`}
            >
              {isLoading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  {(isFree || isPromoFree) ? "Activating..." : "Redirecting..."}
                </>
              ) : isActivePlan ? (
                "Current Plan"
              ) : isPromoLocked ? (
                "Unavailable"
              ) : (
                <>
                  {isFree ? "Get Started Free" : isPromoFree ? "Claim Free" : "Upgrade Now"}
                  <ChevronRight size={16} className="transition-transform group-hover/btn:translate-x-1" />
                </>
              )}
            </button>
            {isActivePlan && activeSubObj && !isPromoFree && !isFree && (
              <div className="flex flex-col mt-2 gap-2 w-full">
                <button
                  onClick={() => handleManageBilling()}
                  disabled={isOpeningPortal || isAnyLoading}
                  className="w-full py-2.5 bg-slate-800 text-white font-bold text-[10px] tracking-widest uppercase hover:bg-slate-700 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {isOpeningPortal ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                  Manage Billing
                </button>
                {(() => {
                  // Filter by BOTH ID and Code to be absolutely sure
                  const planId = String(plan._id || '').trim();
                  const planCode = String(plan.code || '').trim();
                  
                  const matchingSubs = activeSubs.filter((s) => {
                    const sPlanId = String(s.plan?._id || s.plan || '').trim();
                    const sPlanCode = String(s.plan?.code || '').trim();
                    return (planId && sPlanId === planId) || (planCode && sPlanCode === planCode);
                  });
                  
                  const hasAnyCancelled = matchingSubs.some(s => {
                    const sStat = String(s.status || s.state || '').trim().toLowerCase();
                    return !!s.pendingCancelAt || sStat.includes('cancel') || sStat === 'non_renewing';
                  });
                  
                  const hasActive = matchingSubs.some(s => {
                    const sStat = String(s.status || s.state || '').trim().toLowerCase();
                    const isSch = !!s.pendingCancelAt || sStat.includes('cancel') || sStat === 'non_renewing';
                    return sStat === 'active' && !isSch;
                  });
                  
                  // Hide button if ANY record for this plan is cancelled
                  if (hasAnyCancelled) return null;
                  // Hide if NO record is active
                  if (!hasActive) return null;
                  
                  return (
                    <button
                      onClick={() => handleCancelClick(activeSubObj._id)}
                      className="w-full py-2 text-red-500 font-bold text-[10px] tracking-widest uppercase hover:bg-red-50 rounded-xl transition-colors flex items-center justify-center"
                    >
                      Cancel Subscription
                    </button>
                  );
                })()}
              </div>
            )}
          </div>
          <div className="mb-5 flex-1 flex justify-center">
            <div className="space-y-2 inline-flex flex-col">
              {features.length > 0 ? (
                features.map((feature, fIndex) => (
                  <div key={fIndex} className="flex items-start gap-2.5">
                    <div className={`p-0.5 rounded-full mt-0.5 ${isFeatured ? "bg-emerald-100 text-[#247E45]" : "bg-slate-100 text-slate-400"}`}>
                      <Check size={9} strokeWidth={4} />
                    </div>
                    <span className="text-xs text-slate-600 font-medium whitespace-nowrap">{feature}</span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400 italic">Contact us for details.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const gridCols = (count) => {
    if (count <= 2) return "grid-cols-1 md:grid-cols-2";
    if (count === 3) return "grid-cols-1 md:grid-cols-3";
    return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
  };

  // Recommended plan detection — highlight "Performance" plan, or fall back to second-to-last
  const featuredIn = (group) => {
    // 1. Prioritize plan with price 19999
    const priceIdx = group.findIndex((p) => p.price === 19999);
    if (priceIdx >= 0) return priceIdx;

    // 2. Fallback to name-based detection
    const nameIdx = group.findIndex(
      (p) => p.code?.toLowerCase().includes("performance") || p.code?.toLowerCase().includes("foundation")
    );
    if (nameIdx >= 0) return nameIdx;

    // 3. Last fallback: middle of the list
    return Math.max(0, Math.floor(group.length / 2) - 1);
  };

  // ── Gate: checking ─────────────────────────────────────────────────────────
  if (gateStatus === "checking") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8fafc]">
        <Loader2 size={40} className="animate-spin text-[#247E45] mb-4" />
        <p className="text-slate-500 text-sm font-medium">Checking your subscription…</p>
      </div>
    );
  }

  // ── Contextual top banner ───────────────────────────────────────────────────
  const TopBanner = (() => {
    if (gateStatus === "exhausted") {
      return (
        <div className="mb-8 flex-shrink-0">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-2xl p-5 flex items-start gap-4 shadow-sm">
            <div className="text-amber-500 flex-shrink-0 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            </div>
            <div>
              <p className="font-bold text-amber-900 text-sm mb-1">Your plan quota is exhausted</p>
              <p className="text-amber-800 text-xs leading-relaxed">
                You've used all the credits included in your current plan. Upgrade to a paid plan below to continue uploading videos and accessing AI features.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  })();


  return (
    <div
      className="subscription-pricing-page w-full max-w-full bg-[#f8fafc] text-slate-900 relative font-sans selection:bg-emerald-500/10 overflow-x-hidden overflow-y-visible"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none opacity-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-100 blur-3xl rounded-full -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="subscription-pricing-inner w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 lg:pt-32 lg:pb-20 relative z-10 overflow-visible">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-2 text-[#1a1a1a]">
            Elevate Your <span className="text-[#247E45]">Performance</span>
          </h1>
          <p className="text-xs md:text-sm text-slate-500 font-medium uppercase tracking-widest opacity-80">
            Subscription Plans
          </p>
        </div>

        {/* Exhausted plan banner (shown when quota is used up) */}
        {TopBanner}

        <div className={`grid gap-6 lg:gap-8 items-stretch pb-16 ${gridCols(loading ? 4 : individualPlans.length)}`}>
          {loading
            ? [...Array(4)].map((_, i) => <PlanSkeleton key={i} />)
            : individualPlans.map((plan, i) => renderCard(plan, i, featuredIn(individualPlans)))}
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => !isCancelling && setCancelDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1e293b' }}>
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <p className="text-slate-600 mt-2">
            Do you really want to cancel the subscription? You will lose access to premium features once your current billing period ends.
          </p>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            disabled={isCancelling} 
            sx={{ color: '#64748b', fontWeight: 'bold' }}
          >
            No, keep it
          </Button>
          <Button 
            onClick={() => {
              setCancelDialogOpen(false);
              setOpenLetsTalk(true);
            }} 
            disabled={isCancelling} 
            color="error" 
            variant="contained"
            sx={{ fontWeight: 'bold', borderRadius: '8px', padding: '6px 16px', backgroundColor: '#ef4444', '&:hover': { backgroundColor: '#dc2626' } }}
          >
            Yes, cancel
          </Button>
        </DialogActions>
      </Dialog>

      <ContactFormModal 
        isOpen={openLetsTalk}
        onClose={() => setOpenLetsTalk(false)}
        onSubmitSuccess={() => confirmCancelSubscription()}
        variant="cancellation"
      />
    </div>
  );
}
